import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBath,
  faBed,
  faBolt,
  faBuildingUser,
  faCalculator,
  faCalendarAlt,
  faCircleCheck,
  faCog,
  faDroplet,
  faEnvelope,
  faFileLines,
  faGauge,
  faHouseCircleCheck,
  faKey,
  faLocationDot,
  faMoneyBillWave,
  faPenToSquare,
  faPhone,
  faPlus,
  faRulerCombined,
  faSpinner,
  faTint,
  faTriangleExclamation,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../../i18n/LanguageContext";
import { handleSessionExpiry } from "../../../utils/session";
import { formatDate } from "../../../utils/calendar";
import PropertySettings from "../property-settings/property-settings";
import "./property-dashboard.css";

const TABS = [
  { id: "overview", label: "Overview", icon: faBuildingUser },
  { id: "utilities", label: "Utilities", icon: faBolt },
  { id: "settings", label: "Property Settings", icon: faCog },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function PropertyDashboard() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const token = localStorage.getItem("token");
  const [calendarPref, setCalendarPref] = useState(localStorage.getItem("calendarPreference") || "BS");

  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  }, [t]);

  const [activeTab, setActiveTab] = useState("overview");

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [utilityData, setUtilityData] = useState(null);
  const [utilityLoading, setUtilityLoading] = useState(true);
  const [utilityError, setUtilityError] = useState("");
  const [utilityMessage, setUtilityMessage] = useState("");

  const [utilityProfile, setUtilityProfile] = useState({
    electricityMeterNo: "",
    waterMeterNo: "",
    electricityRatePerUnit: "",
    waterRatePerUnit: "",
    electricityStartUnit: "",
    waterStartUnit: "",
    meterStartDate: todayIso(),
    notes: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [meterForms, setMeterForms] = useState({
    electricity: { meterUnit: "", readingDate: todayIso(), notes: "" },
    water: { meterUnit: "", readingDate: todayIso(), notes: "" },
  });
  const [meterSavingType, setMeterSavingType] = useState("");

  const [estimatorUnits, setEstimatorUnits] = useState({ electricity: "", water: "" });

  const [showTermModal, setShowTermModal] = useState(false);
  const [termReason, setTermReason] = useState("");
  const [termSubmitting, setTermSubmitting] = useState(false);
  const [termError, setTermError] = useState("");
  const [termSuccess, setTermSuccess] = useState("");

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    details: [],
    confirmLabel: "",
    cancelLabel: "",
  });
  const confirmResolverRef = useRef(null);

  const openConfirmModal = useCallback((config) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmModal({
        open: true,
        title: config.title || tr("propertyDashboard.confirmTitle", "Please Confirm"),
        message: config.message || "",
        details: Array.isArray(config.details) ? config.details : [],
        confirmLabel: config.confirmLabel || tr("propertyDashboard.proceed", "Proceed"),
        cancelLabel: config.cancelLabel || tr("propertyDashboard.cancel", "Cancel"),
      });
    });
  }, [tr]);

  const closeConfirmModal = useCallback((result) => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(result);
      confirmResolverRef.current = null;
    }
    setConfirmModal((prev) => ({ ...prev, open: false }));
  }, []);

  const fmt = useCallback((dateStr) => {
    if (!dateStr) return "-";
    return formatDate(new Date(dateStr), calendarPref, "en");
  }, [calendarPref]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/owner/properties/${propertyId}/rental-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        handleSessionExpiry(navigate);
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.message || tr("propertyDashboard.failedLoadPropertyData", "Failed to load property data."));
      } else {
        setDashboard(body);
      }
    } catch {
      setError(tr("propertyDashboard.networkErrorTryAgain", "Network error. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [navigate, propertyId, token, tr]);

  const fetchUtilities = useCallback(async () => {
    setUtilityLoading(true);
    setUtilityError("");
    try {
      const res = await fetch(`/api/owner/properties/${propertyId}/utilities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        handleSessionExpiry(navigate);
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUtilityError(body.message || tr("propertyDashboard.failedLoadUtilityData", "Failed to load utility data."));
      } else {
        setUtilityData(body);
      }
    } catch {
      setUtilityError(tr("propertyDashboard.failedLoadUtilityData", "Failed to load utility data."));
    } finally {
      setUtilityLoading(false);
    }
  }, [navigate, propertyId, token, tr]);

  const fetchCalendarPreference = useCallback(async () => {
    try {
      const res = await fetch("/api/user/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const body = await res.json().catch(() => ({}));
      const pref = body?.calendar_preference === "AD" ? "AD" : "BS";
      setCalendarPref(pref);
      localStorage.setItem("calendarPreference", pref);
    } catch {
      // Keep existing local fallback value
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      handleSessionExpiry(navigate);
      return;
    }
    fetchDashboard();
    fetchUtilities();
    fetchCalendarPreference();
  }, [fetchDashboard, fetchUtilities, fetchCalendarPreference, navigate, token]);

  useEffect(() => {
    const p = utilityData?.profile;
    if (!p) return;
    setUtilityProfile({
      electricityMeterNo: p.electricityMeterNo || "",
      waterMeterNo: p.waterMeterNo || "",
      electricityRatePerUnit: String(p.electricityRatePerUnit ?? ""),
      waterRatePerUnit: String(p.waterRatePerUnit ?? ""),
      electricityStartUnit: String(p.electricityStartUnit ?? ""),
      waterStartUnit: String(p.waterStartUnit ?? ""),
      meterStartDate: p.meterStartDate || todayIso(),
      notes: p.notes || "",
    });
  }, [utilityData]);

  const handleSaveUtilityProfile = async () => {
    setUtilityError("");
    setUtilityMessage("");

    try {
      const previousProfile = utilityData?.profile || {};
      const criticalChanges = [];

      const checkChange = (label, prev, next) => {
        if (String(prev ?? "") !== String(next ?? "")) {
          criticalChanges.push(`${label}: ${prev || 0} -> ${next || 0}`);
        }
      };

      checkChange(tr("propertyDashboard.electricityMeterNo", "Electricity Meter No"), previousProfile.electricityMeterNo, utilityProfile.electricityMeterNo);
      checkChange(tr("propertyDashboard.waterMeterNo", "Water Meter No"), previousProfile.waterMeterNo, utilityProfile.waterMeterNo);
      checkChange(tr("propertyDashboard.electricityRate", "Electricity Rate"), previousProfile.electricityRatePerUnit, utilityProfile.electricityRatePerUnit);
      checkChange(tr("propertyDashboard.waterRate", "Water Rate"), previousProfile.waterRatePerUnit, utilityProfile.waterRatePerUnit);
      checkChange(tr("propertyDashboard.electricityStartUnit", "Electricity Start Unit"), previousProfile.electricityStartUnit, utilityProfile.electricityStartUnit);
      checkChange(tr("propertyDashboard.waterStartUnit", "Water Start Unit"), previousProfile.waterStartUnit, utilityProfile.waterStartUnit);

      if (criticalChanges.length > 0) {
        const ok = await openConfirmModal({
          title: tr("propertyDashboard.confirmCriticalUpdateTitle", "Confirm Critical Utility Update"),
          message: tr("propertyDashboard.confirmCriticalUpdateMsg", "You are about to update critical utility numbers."),
          details: criticalChanges,
          confirmLabel: tr("propertyDashboard.updateNow", "Update Now"),
          cancelLabel: tr("propertyDashboard.cancel", "Cancel"),
        });
        if (!ok) {
          return;
        }
      }

      setSavingProfile(true);

      const payload = {
        ...utilityProfile,
        electricityRatePerUnit: Number(utilityProfile.electricityRatePerUnit || 0),
        waterRatePerUnit: Number(utilityProfile.waterRatePerUnit || 0),
        electricityStartUnit: Number(utilityProfile.electricityStartUnit || 0),
        waterStartUnit: Number(utilityProfile.waterStartUnit || 0),
      };
      const res = await fetch(`/api/owner/properties/${propertyId}/utilities/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUtilityError(body.message || tr("propertyDashboard.couldNotSaveUtilityProfile", "Could not save utility profile."));
      } else {
        setUtilityMessage(body.message || tr("propertyDashboard.utilityProfileSaved", "Utility profile saved."));
        fetchUtilities();
      }
    } catch {
      setUtilityError(tr("propertyDashboard.networkErrorSavingUtilityProfile", "Network error while saving utility profile."));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddReading = async (utilityType) => {
    setUtilityError("");
    setUtilityMessage("");
    const form = meterForms[utilityType];
    if (!form?.readingDate || String(form.meterUnit).trim() === "") {
      setUtilityError(tr("propertyDashboard.readingRequired", "Reading date and meter unit are required."));
      return;
    }

    const latest = utilityData?.latest?.[utilityType];
    const fallbackStart = utilityType === "electricity"
      ? Number(utilityProfile.electricityStartUnit || 0)
      : Number(utilityProfile.waterStartUnit || 0);
    const previous = latest ? Number(latest.meterUnit || 0) : fallbackStart;
    const next = Number(form.meterUnit || 0);
    const consumed = Math.max(0, next - previous);
    const rate = utilityType === "electricity"
      ? Number(utilityProfile.electricityRatePerUnit || 0)
      : Number(utilityProfile.waterRatePerUnit || 0);
    const estimated = consumed * rate;

    const confirmed = await openConfirmModal({
      title: tr("propertyDashboard.confirmReadingTitle", "Confirm Meter Reading"),
      message: `${tr("propertyDashboard.utility", "Utility")}: ${utilityType === "electricity" ? tr("propertyDashboard.electricity", "Electricity") : tr("propertyDashboard.water", "Water")}`,
      details: [
        `${tr("propertyDashboard.previousUnit", "Previous unit")}: ${previous}`,
        `${tr("propertyDashboard.newUnit", "New unit")}: ${next}`,
        `${tr("propertyDashboard.consumedUnits", "Consumed units")}: ${consumed}`,
        `${tr("propertyDashboard.estimatedAmount", "Estimated amount")}: NPR ${Math.round(estimated).toLocaleString()}`,
      ],
      confirmLabel: tr("propertyDashboard.saveReading", "Save Reading"),
      cancelLabel: tr("propertyDashboard.cancel", "Cancel"),
    });
    if (!confirmed) return;

    setMeterSavingType(utilityType);
    try {
      const res = await fetch(`/api/owner/properties/${propertyId}/utilities/readings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          utilityType,
          readingDate: form.readingDate,
          meterUnit: Number(form.meterUnit),
          notes: form.notes,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUtilityError(body.message || `${tr("propertyDashboard.couldNotAdd", "Could not add")} ${utilityType === "electricity" ? tr("propertyDashboard.electricity", "Electricity") : tr("propertyDashboard.water", "Water")} ${tr("propertyDashboard.reading", "reading")}.`);
      } else {
        setUtilityMessage(body.message || tr("propertyDashboard.meterReadingAdded", "Meter reading added."));
        setMeterForms((prev) => ({
          ...prev,
          [utilityType]: {
            meterUnit: "",
            readingDate: todayIso(),
            notes: "",
          },
        }));
        fetchUtilities();
      }
    } catch {
      setUtilityError(tr("propertyDashboard.networkErrorAddingReading", "Network error while adding reading."));
    } finally {
      setMeterSavingType("");
    }
  };

  const handleRequestTermination = async () => {
    if (!termReason.trim()) {
      setTermError(tr("propertyDashboard.pleaseProvideReason", "Please provide a reason."));
      return;
    }
    if (!dashboard?.rental?.rentalId) return;

    setTermSubmitting(true);
    setTermError("");
    try {
      const res = await fetch(`/api/rentals/${dashboard.rental.rentalId}/request-termination`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: termReason }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTermError(body.message || tr("propertyDashboard.failedToSubmitRequest", "Failed to submit request."));
      } else {
        setTermSuccess(tr("propertyDashboard.terminationRequestSent", "Termination request sent to the renter."));
        setShowTermModal(false);
        setTermReason("");
        fetchDashboard();
      }
    } catch {
      setTermError(tr("propertyDashboard.networkErrorTryAgain", "Network error. Please try again."));
    } finally {
      setTermSubmitting(false);
    }
  };

  const handleCancelTermination = async () => {
    if (!dashboard?.rental?.rentalId) return;
    try {
      await fetch(`/api/rentals/${dashboard.rental.rentalId}/cancel-termination`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchDashboard();
    } catch {
      // best effort
    }
  };

  const leaseMetrics = useMemo(() => {
    const rental = dashboard?.rental;
    if (!rental) {
      return {
        daysRemaining: null,
        elapsedDays: null,
        totalDays: null,
        progress: 0,
      };
    }
    const now = new Date();
    const start = new Date(rental.startDate);
    const end = new Date(rental.endDate);
    const totalDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000));
    const elapsedDays = Math.max(0, Math.min(totalDays, Math.ceil((now.getTime() - start.getTime()) / 86400000)));
    const daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
    const progress = Math.max(0, Math.min(100, Math.round((elapsedDays / totalDays) * 100)));
    return { daysRemaining, elapsedDays, totalDays, progress };
  }, [dashboard]);

  const utilityTotals = utilityData?.totals || {
    electricityUnits: 0,
    electricityAmount: 0,
    waterUnits: 0,
    waterAmount: 0,
    overallAmount: 0,
  };

  const monthlyRecords = utilityData?.monthlyRecords || [];

  const thisMonthKey = new Date().toISOString().slice(0, 7);
  const thisMonthRecord = monthlyRecords.find((item) => item.month === thisMonthKey) || null;

  const estimator = useMemo(() => {
    const eRate = Number(utilityProfile.electricityRatePerUnit || 0);
    const wRate = Number(utilityProfile.waterRatePerUnit || 0);
    const eUnits = Number(estimatorUnits.electricity || 0);
    const wUnits = Number(estimatorUnits.water || 0);
    const eAmount = Math.max(0, eUnits) * Math.max(0, eRate);
    const wAmount = Math.max(0, wUnits) * Math.max(0, wRate);
    return {
      eUnits,
      wUnits,
      eAmount,
      wAmount,
      total: eAmount + wAmount,
    };
  }, [estimatorUnits, utilityProfile.electricityRatePerUnit, utilityProfile.waterRatePerUnit]);

  const verificationChip = (status) => {
    const map = {
      approved: { label: tr("propertyDashboard.verified", "Verified"), cls: "pd-chip--green" },
      pending: { label: tr("propertyDashboard.verificationPending", "Verification Pending"), cls: "pd-chip--amber" },
      rejected: { label: tr("propertyDashboard.rejected", "Rejected"), cls: "pd-chip--red" },
    };
    const meta = map[status] || { label: tr("propertyDashboard.notVerified", "Not Verified"), cls: "pd-chip--blue" };
    return <span className={`pd-chip ${meta.cls}`}>{meta.label}</span>;
  };

  if (loading) {
    return (
      <div className="pd-shell pd-shell--loading">
        <FontAwesomeIcon icon={faSpinner} spin className="pd-spinner" />
        <p>{tr("propertyDashboard.loading", "Loading property dashboard...")}</p>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="pd-shell pd-shell--error">
        <p>{error || tr("propertyDashboard.loadFailed", "Could not load property dashboard.")}</p>
        <button className="pd-btn-primary" onClick={fetchDashboard}>{tr("common.retry", "Retry")}</button>
        <Link to="/owner-dashboard" className="pd-back-link">{tr("propertyDashboard.backOwnerDashboard", "Back to Owner Dashboard")}</Link>
      </div>
    );
  }

  const rental = dashboard.rental;

  return (
    <div className="pd-shell">
      <div className="pd-topbar">
        <button className="pd-back-btn" onClick={() => navigate("/owner-dashboard")}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{tr("propertyDashboard.ownerDashboard", "Owner Dashboard")}</span>
        </button>
        <div className="pd-topbar-title">
          <h1>{dashboard.name || tr("propertyDashboard.propertyDashboardTitle", "Property Dashboard")}</h1>
          <div className="pd-topbar-meta">
            <span className="pd-chip pd-chip--blue">
              <FontAwesomeIcon icon={faLocationDot} />
              {[dashboard.city, dashboard.state].filter(Boolean).join(", ") || tr("propertyDashboard.locationNotSet", "Location not set")}
            </span>
            {verificationChip(dashboard.verificationStatus)}
            <span className={`pd-chip ${rental ? "pd-chip--indigo" : "pd-chip--green"}`}>
              <FontAwesomeIcon icon={rental ? faKey : faHouseCircleCheck} />
              {rental ? tr("propertyDashboard.rented", "Rented") : tr("propertyDashboard.vacant", "Vacant")}
            </span>
          </div>
        </div>
      </div>

      <div className="pd-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`pd-tab${activeTab === tab.id ? " pd-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            {tab.id === "overview"
              ? tr("propertyDashboard.tabOverview", "Overview")
              : tab.id === "utilities"
              ? tr("propertyDashboard.tabUtilities", "Utilities")
              : tr("propertyDashboard.tabSettings", "Property Settings")}
          </button>
        ))}
      </div>

      <div className="pd-content">
        {activeTab === "overview" && (
          <>
            <div className="pd-stat-row">
              <div className="pd-stat-card">
                <FontAwesomeIcon icon={faMoneyBillWave} className="pd-stat-icon pd-stat-icon--green" />
                <div>
                  <div className="pd-stat-value">NPR {(rental?.monthlyRent || dashboard.price || 0).toLocaleString()}</div>
                  <div className="pd-stat-label">{tr("propertyDashboard.monthlyRent", "Monthly Rent")}</div>
                </div>
              </div>
              <div className="pd-stat-card">
                <FontAwesomeIcon icon={faCalculator} className="pd-stat-icon pd-stat-icon--amber" />
                <div>
                  <div className="pd-stat-value">NPR {Math.round(utilityTotals.overallAmount || 0).toLocaleString()}</div>
                  <div className="pd-stat-label">{tr("propertyDashboard.utilitiesBilledTracked", "Utilities Billed (Tracked)")}</div>
                </div>
              </div>
              <div className="pd-stat-card">
                <FontAwesomeIcon icon={faCalculator} className="pd-stat-icon pd-stat-icon--blue" />
                <div>
                  <div className="pd-stat-value">NPR {Math.round(thisMonthRecord?.totalAmount || 0).toLocaleString()}</div>
                  <div className="pd-stat-label">{tr("propertyDashboard.thisMonthUtilities", "This Month Utilities")}</div>
                </div>
              </div>
              <div className="pd-stat-card">
                <FontAwesomeIcon icon={faGauge} className="pd-stat-icon pd-stat-icon--blue" />
                <div>
                  <div className="pd-stat-value">{leaseMetrics.progress}%</div>
                  <div className="pd-stat-label">{tr("propertyDashboard.leaseProgress", "Lease Progress")}</div>
                </div>
              </div>
              <div className="pd-stat-card">
                <FontAwesomeIcon icon={faCalendarAlt} className="pd-stat-icon pd-stat-icon--purple" />
                <div>
                  <div className="pd-stat-value">
                    {leaseMetrics.daysRemaining !== null ? leaseMetrics.daysRemaining : "-"}
                  </div>
                  <div className="pd-stat-label">{tr("propertyDashboard.daysRemaining", "Days Remaining")}</div>
                </div>
              </div>
              {dashboard.pendingApplications > 0 && (
                <div className="pd-stat-card">
                  <FontAwesomeIcon icon={faFileLines} className="pd-stat-icon pd-stat-icon--amber" />
                  <div>
                    <div className="pd-stat-value">{dashboard.pendingApplications}</div>
                    <div className="pd-stat-label">{tr("propertyDashboard.pendingApplications", "Pending Applications")}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="pd-progress-card">
              <div className="pd-progress-head">
                <strong>{tr("propertyDashboard.leaseTimeline", "Lease Timeline")}</strong>
                {rental ? (
                  <span>{fmt(rental.startDate)} {tr("propertyDashboard.to", "to")} {fmt(rental.endDate)}</span>
                ) : (
                  <span>{tr("propertyDashboard.noActiveLease", "No active lease")}</span>
                )}
              </div>
              <div className="pd-progress-track">
                <div className="pd-progress-fill" style={{ width: `${leaseMetrics.progress}%` }} />
              </div>
              <div className="pd-progress-foot">
                <span>{tr("propertyDashboard.elapsed", "Elapsed")}: {leaseMetrics.elapsedDays ?? 0} {tr("propertyDashboard.daysShort", "day(s)")}</span>
                <span>{tr("propertyDashboard.total", "Total")}: {leaseMetrics.totalDays ?? 0} {tr("propertyDashboard.daysShort", "day(s)")}</span>
              </div>
            </div>

            {rental ? (
              <div className="pd-rental-card">
                <div className="pd-rental-card-header">
                  <FontAwesomeIcon icon={faBuildingUser} />
                  <h3>{tr("propertyDashboard.activeTenancyAndPaymentContext", "Active Tenancy and Payment Context")}</h3>
                  {rental.terminationRequested && (
                    <span className="pd-chip pd-chip--red pd-chip--sm">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      {tr("propertyDashboard.terminationRequested", "Termination Requested")}
                    </span>
                  )}
                </div>

                {rental.terminationRequested && (
                  <div className="pd-termination-banner">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <div>
                      <strong>{tr("propertyDashboard.terminationInProgress", "Termination in progress")}</strong>
                      <p>
                        {tr("propertyDashboard.requestedBy", "Requested by")}: {rental.terminationRequestedBy === "owner" ? tr("propertyDashboard.you", "You") : tr("propertyDashboard.tenant", "Tenant")} | {fmt(rental.terminationRequestedAt)}
                      </p>
                      {rental.terminationReason && <p>{tr("propertyDashboard.reason", "Reason")}: {rental.terminationReason}</p>}
                    </div>
                    {rental.terminationRequestedBy === "owner" && (
                      <button className="pd-btn-ghost pd-btn-sm" onClick={handleCancelTermination}>{tr("propertyDashboard.cancelRequest", "Cancel Request")}</button>
                    )}
                  </div>
                )}

                <div className="pd-rental-grid">
                  <div className="pd-info-section">
                    <h4><FontAwesomeIcon icon={faUser} /> {tr("propertyDashboard.tenantInformation", "Tenant Information")}</h4>
                    <div className="pd-info-list">
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.fullName", "Full Name")}</span>
                        <span className="pd-info-value">{rental.renter?.name || "-"}</span>
                      </div>
                      <div className="pd-info-row">
                        <FontAwesomeIcon icon={faEnvelope} className="pd-row-icon" />
                        <span className="pd-info-label">{tr("propertyDashboard.email", "Email")}</span>
                        <span className="pd-info-value">{rental.renter?.email || "-"}</span>
                      </div>
                      <div className="pd-info-row">
                        <FontAwesomeIcon icon={faPhone} className="pd-row-icon" />
                        <span className="pd-info-label">{tr("propertyDashboard.phone", "Phone")}</span>
                        <span className="pd-info-value">{rental.renter?.phone || "-"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="pd-info-section">
                    <h4><FontAwesomeIcon icon={faCalendarAlt} /> {tr("propertyDashboard.leaseDetails", "Lease Details")}</h4>
                    <div className="pd-info-list">
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.startDate", "Start Date")}</span>
                        <span className="pd-info-value">{fmt(rental.startDate)}</span>
                      </div>
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.endDate", "End Date")}</span>
                        <span className="pd-info-value">{fmt(rental.endDate)}</span>
                      </div>
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.status", "Status")}</span>
                        <span className="pd-info-value">
                          <span className={`pd-chip ${rental.rentalStatus === "active" ? "pd-chip--green" : "pd-chip--amber"} pd-chip--sm`}>
                            {rental.rentalStatus || tr("propertyDashboard.unknown", "unknown")}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pd-info-section">
                    <h4><FontAwesomeIcon icon={faMoneyBillWave} /> {tr("propertyDashboard.paymentSummary", "Payment Summary")}</h4>
                    <div className="pd-info-list">
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.monthlyRent", "Monthly Rent")}</span>
                        <span className="pd-info-value pd-info-value--strong">NPR {(rental.monthlyRent || 0).toLocaleString()}</span>
                      </div>
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.utilityTracked", "Utility Tracked")}</span>
                        <span className="pd-info-value">NPR {Math.round(utilityTotals.overallAmount || 0).toLocaleString()}</span>
                      </div>
                      <div className="pd-info-row">
                        <span className="pd-info-label">{tr("propertyDashboard.calendar", "Calendar")}</span>
                        <span className="pd-info-value">
                          {calendarPref === "BS" ? tr("propertyDashboard.bikramSambat", "Bikram Sambat") : "AD"} | <Link to="/profile" className="pd-link-sm">{tr("propertyDashboard.change", "Change")}</Link>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {!rental.terminationRequested && (
                  <div className="pd-rental-actions">
                    <button className="pd-btn-danger pd-btn-sm" onClick={() => { setShowTermModal(true); setTermError(""); setTermSuccess(""); }}>
                      <FontAwesomeIcon icon={faKey} /> {tr("propertyDashboard.requestLeaseTermination", "Request Lease Termination")}
                    </button>
                  </div>
                )}

                {termSuccess && <div className="pd-alert pd-alert--success">{termSuccess}</div>}
              </div>
            ) : (
              <div className="pd-vacant-card">
                <FontAwesomeIcon icon={faHouseCircleCheck} className="pd-vacant-icon" />
                <h3>{tr("propertyDashboard.propertyIsVacant", "Property is Vacant")}</h3>
                <p>{tr("propertyDashboard.noActiveTenancyDesc", "No active tenancy. You can still configure utility rates and meter baseline in the Utilities tab.")}</p>
                <button className="pd-btn-primary" onClick={() => setActiveTab("utilities")}>{tr("propertyDashboard.configureUtilities", "Configure Utilities")}</button>
              </div>
            )}

            <div className="pd-property-details-card">
              <h3>{tr("propertyDashboard.propertySnapshot", "Property Snapshot")}</h3>
              <div className="pd-snapshot-grid">
                <div className="pd-snapshot-item"><FontAwesomeIcon icon={faBed} /> <span>{dashboard.bedrooms ?? "-"} {tr("propertyDashboard.bed", "Bed")}</span></div>
                <div className="pd-snapshot-item"><FontAwesomeIcon icon={faBath} /> <span>{dashboard.bathrooms ?? "-"} {tr("propertyDashboard.bath", "Bath")}</span></div>
                <div className="pd-snapshot-item"><FontAwesomeIcon icon={faRulerCombined} /> <span>{dashboard.sqft ?? "-"} {tr("propertyDashboard.sqft", "sq.ft")}</span></div>
                <div className="pd-snapshot-item"><FontAwesomeIcon icon={faLocationDot} /> <span>{dashboard.city || tr("propertyDashboard.locationPending", "Location pending")}</span></div>
              </div>
              {dashboard.description && <p className="pd-property-desc">{dashboard.description}</p>}
              {Array.isArray(dashboard.amenities) && dashboard.amenities.length > 0 && (
                <div className="pd-amenities-row">
                  {dashboard.amenities.map((item) => <span key={item} className="pd-amenity-chip">{item}</span>)}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "utilities" && (
          <>
            <div className="pd-utility-header">
              <h2>{tr("propertyDashboard.utilityBillingTitle", "Utility Billing and Meter Intelligence")}</h2>
              <p>{tr("propertyDashboard.utilityBillingDesc", "Track electricity/water meter units, update tariffs, estimate bills, and keep a clean reading history.")}</p>
            </div>

            {utilityLoading ? (
              <div className="pd-loading-inline"><FontAwesomeIcon icon={faSpinner} spin /> {tr("propertyDashboard.loadingUtilityData", "Loading utility data...")}</div>
            ) : (
              <>
                {(utilityError || utilityMessage) && (
                  <div className={`pd-alert ${utilityError ? "pd-alert--error" : "pd-alert--success"}`}>
                    {utilityError || utilityMessage}
                  </div>
                )}

                <div className="pd-utility-grid">
                  <div className="pd-utility-card">
                    <h3><FontAwesomeIcon icon={faPenToSquare} /> {tr("propertyDashboard.utilityProfileSetup", "Utility Profile Setup")}</h3>
                    <p className="pd-muted">{tr("propertyDashboard.utilityProfileSetupDesc", "Define meter numbers, baseline units at lease start, and per-unit rates.")}</p>
                    <div className="pd-form-grid">
                      <label>
                        {tr("propertyDashboard.electricityMeterNo", "Electricity Meter No")}
                        <input
                          value={utilityProfile.electricityMeterNo}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, electricityMeterNo: e.target.value }))}
                          placeholder={tr("propertyDashboard.electricityMeterNoPlaceholder", "e.g., NEA-12234")}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.waterMeterNo", "Water Meter No")}
                        <input
                          value={utilityProfile.waterMeterNo}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, waterMeterNo: e.target.value }))}
                          placeholder={tr("propertyDashboard.waterMeterNoPlaceholder", "e.g., KUKL-7788")}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.electricityRatePerUnit", "Electricity Rate per Unit (NPR)")}
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={utilityProfile.electricityRatePerUnit}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, electricityRatePerUnit: e.target.value }))}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.waterRatePerUnit", "Water Rate per Unit (NPR)")}
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={utilityProfile.waterRatePerUnit}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, waterRatePerUnit: e.target.value }))}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.electricityStartUnit", "Electricity Start Unit")}
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={utilityProfile.electricityStartUnit}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, electricityStartUnit: e.target.value }))}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.waterStartUnit", "Water Start Unit")}
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={utilityProfile.waterStartUnit}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, waterStartUnit: e.target.value }))}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.meterStartDate", "Meter Start Date")}
                        <input
                          type="date"
                          value={utilityProfile.meterStartDate}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, meterStartDate: e.target.value }))}
                        />
                      </label>
                      <label className="pd-form-col-full">
                        {tr("propertyDashboard.notes", "Notes")}
                        <textarea
                          rows={3}
                          value={utilityProfile.notes}
                          onChange={(e) => setUtilityProfile((prev) => ({ ...prev, notes: e.target.value }))}
                          placeholder={tr("propertyDashboard.optionalBillingRemarks", "Optional billing remarks")}
                        />
                      </label>
                    </div>
                    <button className="pd-btn-primary" onClick={handleSaveUtilityProfile} disabled={savingProfile}>
                      <FontAwesomeIcon icon={savingProfile ? faSpinner : faCircleCheck} spin={savingProfile} />
                      {savingProfile ? tr("propertyDashboard.saving", "Saving...") : tr("propertyDashboard.saveUtilityProfile", "Save Utility Profile")}
                    </button>
                  </div>

                  <div className="pd-utility-card">
                    <h3><FontAwesomeIcon icon={faCalculator} /> {tr("propertyDashboard.priceEstimator", "Price Estimator")}</h3>
                    <p className="pd-muted">{tr("propertyDashboard.priceEstimatorDesc", "Instant estimate from rates x unit consumption.")}</p>
                    <div className="pd-form-grid">
                      <label>
                        {tr("propertyDashboard.electricityUnitsKwh", "Electricity Units (kWh)")}
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={estimatorUnits.electricity}
                          onChange={(e) => setEstimatorUnits((prev) => ({ ...prev, electricity: e.target.value }))}
                        />
                      </label>
                      <label>
                        {tr("propertyDashboard.waterUnitsOnly", "Water Units")}
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={estimatorUnits.water}
                          onChange={(e) => setEstimatorUnits((prev) => ({ ...prev, water: e.target.value }))}
                        />
                      </label>
                    </div>
                    <div className="pd-estimate-boxes">
                      <div className="pd-estimate-item">
                        <span><FontAwesomeIcon icon={faBolt} /> {tr("propertyDashboard.electricity", "Electricity")}</span>
                        <strong>NPR {Math.round(estimator.eAmount).toLocaleString()}</strong>
                      </div>
                      <div className="pd-estimate-item">
                        <span><FontAwesomeIcon icon={faTint} /> Water</span>
                        <strong>NPR {Math.round(estimator.wAmount).toLocaleString()}</strong>
                      </div>
                      <div className="pd-estimate-item pd-estimate-item--total">
                        <span>{tr("propertyDashboard.totalEstimate", "Total Estimate")}</span>
                        <strong>NPR {Math.round(estimator.total).toLocaleString()}</strong>
                      </div>
                    </div>

                    <div className="pd-latest-readings">
                      <h4>{tr("propertyDashboard.latestRecordedMeters", "Latest Recorded Meters")}</h4>
                      <div className="pd-latest-row">
                        <span><FontAwesomeIcon icon={faBolt} /> Electricity</span>
                        <span>
                          {utilityData?.latest?.electricity
                            ? `${utilityData.latest.electricity.meterUnit} ${tr("propertyDashboard.units", "units")} ${tr("propertyDashboard.on", "on")} ${fmt(utilityData.latest.electricity.readingDate)}`
                            : tr("propertyDashboard.noReadingYet", "No reading yet")}
                        </span>
                      </div>
                      <div className="pd-latest-row">
                        <span><FontAwesomeIcon icon={faDroplet} /> {tr("propertyDashboard.water", "Water")}</span>
                        <span>
                          {utilityData?.latest?.water
                            ? `${utilityData.latest.water.meterUnit} ${tr("propertyDashboard.units", "units")} ${tr("propertyDashboard.on", "on")} ${fmt(utilityData.latest.water.readingDate)}`
                            : tr("propertyDashboard.noReadingYet", "No reading yet")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pd-meter-grid">
                  {["electricity", "water"].map((type) => (
                    <div className="pd-meter-card" key={type}>
                      <h3>
                        <FontAwesomeIcon icon={type === "electricity" ? faBolt : faTint} />
                        {tr("propertyDashboard.add", "Add")} {type === "electricity" ? tr("propertyDashboard.electricity", "Electricity") : tr("propertyDashboard.water", "Water")} {tr("propertyDashboard.meterReading", "Meter Reading")}
                      </h3>
                      <div className="pd-form-grid">
                        <label>
                          {tr("propertyDashboard.readingDate", "Reading Date")}
                          <input
                            type="date"
                            value={meterForms[type].readingDate}
                            onChange={(e) => setMeterForms((prev) => ({
                              ...prev,
                              [type]: { ...prev[type], readingDate: e.target.value },
                            }))}
                          />
                        </label>
                        <label>
                          {tr("propertyDashboard.meterUnit", "Meter Unit")}
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={meterForms[type].meterUnit}
                            onChange={(e) => setMeterForms((prev) => ({
                              ...prev,
                              [type]: { ...prev[type], meterUnit: e.target.value },
                            }))}
                            placeholder={tr("propertyDashboard.currentMeterUnit", "Current meter unit")}
                          />
                        </label>
                        <label className="pd-form-col-full">
                          {tr("propertyDashboard.notes", "Notes")}
                          <input
                            value={meterForms[type].notes}
                            onChange={(e) => setMeterForms((prev) => ({
                              ...prev,
                              [type]: { ...prev[type], notes: e.target.value },
                            }))}
                            placeholder={tr("propertyDashboard.optionalNote", "Optional note")}
                          />
                        </label>
                      </div>
                      <button
                        className="pd-btn-primary"
                        onClick={() => handleAddReading(type)}
                        disabled={meterSavingType === type}
                      >
                        <FontAwesomeIcon icon={meterSavingType === type ? faSpinner : faPlus} spin={meterSavingType === type} />
                        {meterSavingType === type ? tr("propertyDashboard.saving", "Saving...") : tr("propertyDashboard.saveReading", "Save Reading")}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pd-history-card">
                  <h3>{tr("propertyDashboard.utilityReadingHistory", "Utility Reading History")}</h3>
                  <div className="pd-history-wrap">
                    <table className="pd-history-table">
                      <thead>
                        <tr>
                          <th>{tr("propertyDashboard.date", "Date")}</th>
                          <th>{tr("propertyDashboard.utility", "Utility")}</th>
                          <th>{tr("propertyDashboard.meterUnit", "Meter Unit")}</th>
                          <th>{tr("propertyDashboard.consumed", "Consumed")}</th>
                          <th>{tr("propertyDashboard.rate", "Rate")}</th>
                          <th>{tr("propertyDashboard.amount", "Amount")}</th>
                          <th>{tr("propertyDashboard.notes", "Notes")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(utilityData?.history || []).length === 0 ? (
                          <tr>
                            <td colSpan={7} className="pd-empty-cell">{tr("propertyDashboard.noReadingsYet", "No readings yet.")}</td>
                          </tr>
                        ) : (
                          utilityData.history.map((row) => (
                            <tr key={row.id}>
                              <td>{fmt(row.readingDate)}</td>
                              <td>{row.utilityType === "electricity" ? tr("propertyDashboard.electricity", "Electricity") : tr("propertyDashboard.water", "Water")}</td>
                              <td>{row.meterUnit}</td>
                              <td>{row.consumedUnits}</td>
                              <td>NPR {Number(row.ratePerUnit || 0).toLocaleString()}</td>
                              <td>NPR {Math.round(Number(row.amount || 0)).toLocaleString()}</td>
                              <td>{row.notes || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="pd-history-card">
                  <h3>{tr("propertyDashboard.monthlyUtilityRecords", "Monthly Utility Records")}</h3>
                  <div className="pd-history-wrap">
                    <table className="pd-history-table">
                      <thead>
                        <tr>
                          <th>{tr("propertyDashboard.month", "Month")}</th>
                          <th>{tr("propertyDashboard.electricityUnits", "Electricity Units")}</th>
                          <th>{tr("propertyDashboard.electricityAmount", "Electricity Amount")}</th>
                          <th>{tr("propertyDashboard.waterUnits", "Water Units")}</th>
                          <th>{tr("propertyDashboard.waterAmount", "Water Amount")}</th>
                          <th>{tr("propertyDashboard.totalAmount", "Total Amount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyRecords.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="pd-empty-cell">{tr("propertyDashboard.noMonthlyRecords", "No monthly records yet.")}</td>
                          </tr>
                        ) : (
                          monthlyRecords.map((row) => (
                            <tr key={row.month}>
                              <td>{row.month}</td>
                              <td>{Number(row.electricityUnits || 0).toFixed(3)}</td>
                              <td>NPR {Math.round(Number(row.electricityAmount || 0)).toLocaleString()}</td>
                              <td>{Number(row.waterUnits || 0).toFixed(3)}</td>
                              <td>NPR {Math.round(Number(row.waterAmount || 0)).toLocaleString()}</td>
                              <td>NPR {Math.round(Number(row.totalAmount || 0)).toLocaleString()}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === "settings" && (
          <div className="pd-settings-wrapper">
            <PropertySettings embeddedPropertyId={propertyId} onBack={() => setActiveTab("overview")} />
          </div>
        )}
      </div>

      {confirmModal.open && (
        <div className="pd-modal-backdrop" onClick={() => closeConfirmModal(false)} role="dialog" aria-modal="true">
          <div className="pd-modal pd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header pd-confirm-header">
              <h4><FontAwesomeIcon icon={faTriangleExclamation} /> {confirmModal.title}</h4>
              <button className="pd-modal-close" onClick={() => closeConfirmModal(false)} aria-label={tr("propertyDashboard.close", "Close")}>x</button>
            </div>
            <div className="pd-modal-body">
              <p className="pd-modal-info">{confirmModal.message}</p>
              {confirmModal.details.length > 0 && (
                <div className="pd-confirm-details">
                  {confirmModal.details.map((line, idx) => (
                    <div className="pd-confirm-detail-row" key={`${line}-${idx}`}>{line}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="pd-modal-footer">
              <button className="pd-btn-ghost" onClick={() => closeConfirmModal(false)}>
                {confirmModal.cancelLabel || tr("propertyDashboard.cancel", "Cancel")}
              </button>
              <button className="pd-btn-primary" onClick={() => closeConfirmModal(true)}>
                {confirmModal.confirmLabel || tr("propertyDashboard.proceed", "Proceed")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTermModal && (
        <div className="pd-modal-backdrop" onClick={() => setShowTermModal(false)} role="dialog" aria-modal="true">
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <h4><FontAwesomeIcon icon={faKey} /> {tr("propertyDashboard.requestLeaseTermination", "Request Lease Termination")}</h4>
              <button className="pd-modal-close" onClick={() => setShowTermModal(false)} aria-label={tr("propertyDashboard.close", "Close")}>x</button>
            </div>
            <div className="pd-modal-body">
              <p className="pd-modal-info">
                {tr("propertyDashboard.terminationModalMsgPrefix", "This sends a termination request to")} <strong>{rental?.renter?.name || tr("propertyDashboard.tenant", "tenant")}</strong>. {tr("propertyDashboard.terminationModalMsgSuffix", "The lease ends only after approval.")}
              </p>
              <label htmlFor="pd-term-reason">{tr("propertyDashboard.reason", "Reason")} <span className="pd-required">*</span></label>
              <textarea
                id="pd-term-reason"
                className="pd-textarea"
                rows={4}
                placeholder={tr("propertyDashboard.terminationReasonPlaceholder", "Provide reason for lease termination")}
                value={termReason}
                onChange={(e) => setTermReason(e.target.value)}
              />
              {termError && <div className="pd-alert pd-alert--error">{termError}</div>}
            </div>
            <div className="pd-modal-footer">
              <button className="pd-btn-ghost" onClick={() => setShowTermModal(false)} disabled={termSubmitting}>{tr("propertyDashboard.cancel", "Cancel")}</button>
              <button className="pd-btn-danger" onClick={handleRequestTermination} disabled={termSubmitting || !termReason.trim()}>
                {termSubmitting ? <><FontAwesomeIcon icon={faSpinner} spin /> {tr("propertyDashboard.sending", "Sending...")}</> : tr("propertyDashboard.sendRequest", "Send Request")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
